import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import posthog from 'posthog-js';
import api from '../api/apiHelper';

const CookieConsentContext = createContext(null);

// --- STAŁE ---
// Klucz w LocalStorage. Zmieniłem na 'v3_production', żeby na 100% wymusić czysty start u każdego
const CONSENT_STORAGE_KEY = 'user_consent_v3_production'; 
const ANONYMOUS_ID_KEY = 'app_anonymous_id';

// --- Funkcje Pomocnicze ---
function generateUUID() {
  return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  );
}

export const CookieConsentProvider = ({ children }) => {
    const [showBanner, setShowBanner] = useState(false);
    const [consentState, setConsentState] = useState({ necessary: true, analytics: false, marketing: false });
    const [isConsentLoaded, setIsConsentLoaded] = useState(false);

    const manageScripts = useCallback((state) => {
    }, []);
    // --- LOGIKA STARTOWA ---
    useEffect(() => {
        const isNative = Capacitor.isNativePlatform(); // True dla iOS i Android

        // 1. GENEROWANIE ID ANONIMOWEGO
        let anonId = localStorage.getItem(ANONYMOUS_ID_KEY);
        if (!anonId) {
            anonId = generateUUID();
            localStorage.setItem(ANONYMOUS_ID_KEY, anonId);
        }

        // 2. SPRAWDZAMY CZY MAMY JUŻ ZAPISANĄ DECYZJĘ
        const savedConsentJSON = localStorage.getItem(CONSENT_STORAGE_KEY);

        if (savedConsentJSON) {
            // --- SCENARIUSZ A: USER JUŻ DECYDOWAŁ (WRACAJĄCY) ---
            const parsedState = JSON.parse(savedConsentJSON);
            setConsentState(parsedState);
            setShowBanner(false); 

            // Odpalamy lub gasimy PostHoga w zależności od zapisanej decyzji
            if (parsedState.analytics) posthog.opt_in_capturing();
            else posthog.opt_out_capturing();

        } else {
            // --- SCENARIUSZ B: NOWY USER (PIERWSZE URUCHOMIENIE) ---
            
            if (isNative) {
                // >>> MOBILE (iOS & ANDROID) <<<
                // Nie pokazujemy baneru. Zakładamy zgodę przez regulamin (Implicit Consent).
                console.log('Mobile Environment: Auto-enabling Analytics.');
                
                const autoConsent = { necessary: true, analytics: true, marketing: false };
                
                // Zapisujemy od razu zgodę
                localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(autoConsent));
                setConsentState(autoConsent);
                setShowBanner(false);
                
                // Włączamy PostHoga NATYCHMIAST
                posthog.opt_in_capturing();

                // Wysyłamy log do API w tle
                sendConsentToAPI(autoConsent, anonId, 'MOBILE_AUTO_START');

            } else {
                // >>> WEB (PRZEGLĄDARKA) <<<
                // Pokazujemy baner. PostHog domyślnie wyłączony.
                console.log('Web Environment: Waiting for Cookie Banner.');
                setShowBanner(true);
                posthog.opt_out_capturing();
            }
        }

        setIsConsentLoaded(true);
    }, []);

    // --- FUNKCJA ZAPISU (DLA BANERU WEBOWEGO) ---
    const handleSaveConsent = useCallback((newConsentState) => {
        console.log("===> handleSaveConsent: Zapisuję wybór usera.");
        
        // 1. UI
        setShowBanner(false);
        setConsentState(newConsentState);
        
        // 2. STORAGE
        localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(newConsentState));

        // 3. POSTHOG
        if (newConsentState.analytics) {
            posthog.opt_in_capturing();
        } else {
            posthog.opt_out_capturing();
        }

        // 4. API & SCRIPTS
        const anonId = localStorage.getItem(ANONYMOUS_ID_KEY);
        sendConsentToAPI(newConsentState, anonId, 'WEB_USER_ACTION');
        manageScripts(newConsentState);
            
    }, [manageScripts]);

    // Synchronizacja ze stanem konta (analytics_allowed z backendu).
    // Musi działać w OBIE strony: wcześniej istniało tylko wyłączanie, więc raz zgaszona
    // analityka nie miała jak wrócić — nawet po ponownym wyrażeniu zgody w ustawieniach.
    const syncAnalyticsWithAccount = useCallback((allowed) => {
        if (typeof allowed !== 'boolean') return;

        let base = { necessary: true, analytics: false, marketing: false };
        try {
            const saved = localStorage.getItem(CONSENT_STORAGE_KEY);
            if (saved) base = JSON.parse(saved);
        } catch (e) {
            console.warn("Niepoprawny zapis zgód w localStorage, startuję od domyślnych.");
        }

        const syncedState = { ...base, analytics: allowed };
        localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(syncedState));
        setConsentState(syncedState);
        setShowBanner(false);

        // Fizycznie włączamy lub gasimy PostHoga
        if (allowed) posthog.opt_in_capturing();
        else posthog.opt_out_capturing();
    }, []);

    // Funkcja pomocnicza do API (Fire and forget)
    const sendConsentToAPI = (details, anonId, source) => {
        const consentPayload = {
            consent_type: 'COOKIE',
            status: 'GRANTED',
            version: '2.0', 
            details: details,
            anonymous_id: anonId,
            source: source 
        };

        // Nie blokujemy UI, to leci w tle
        api.post('/record-consent/', consentPayload, { skipLoading: true })
            .then(() => console.log("API success consent log."))
            .catch((err) => console.warn("API error consent log (non-critical):", err));
    };

    return (
        <CookieConsentContext.Provider value={{ consentState, handleSaveConsent, isConsentLoaded, showBanner, syncAnalyticsWithAccount }}>
            {children}
        </CookieConsentContext.Provider>
    );
};

export const useCookieConsent = () => {
    const context = useContext(CookieConsentContext);
    if (context === undefined) {
        throw new Error('useCookieConsent must be used within a CookieConsentProvider');
    }
    return context;
};